import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axiosClient from '../../api/axiosClient';
import { Package, Plus, Trash2, Edit2, Monitor, Clock } from 'lucide-react';

const PackagesPage = () => {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null); 
  
  // Form State
  const initialFormState = {
    name: '', description: '', billing_cycle: 'one_time', duration_days: 365,
    max_devices: 1, price_usd: 0, price_inr: 0, features: [],
    can_autokill: false, 
    can_isolate: false, 
    can_quarantine: false, 
    can_schedule_scan: false, 
    has_vulnerability_scanner: false, 
    has_watchtower: false, 
    has_export_logs: false,
    has_proactive_hunting: false,
    has_threat_detection: false,
    has_router_detection: false,
    has_supply_chain_detection: false,
    has_ml_evasion_detection: false,
    has_soc_access: false,
    // Mobile Section (Added based on your DB columns)
    has_mobile_protection: false, 
    has_spam_blocker: false, 
    has_anti_theft: false, 
    has_overlay_guard: false, 
    has_netwall_pro: false,
    has_crypto_guard: false,
    has_immortal_mode: false
  };
  const [formData, setFormData] = useState(initialFormState);

  // Helper to set presets
  const applyPreset = (days, label, cycle) => {
    setFormData(prev => ({
      ...prev,
      duration_days: days,
      billing_cycle: cycle,
      name: prev.name || label
    }));
  };

  const { data: packages = [] } = useQuery({
    queryKey: ['packages'],
    queryFn: () => axiosClient.get('/admin/packages')
  });

  const createMutation = useMutation({
    mutationFn: (data) => axiosClient.post('/admin/packages', data),
    onSuccess: () => {
      queryClient.invalidateQueries(['packages']);
      resetForm();
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => axiosClient.put(`/admin/packages/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['packages']);
      resetForm();
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => axiosClient.delete(`/admin/packages/${id}`),
    onSuccess: () => queryClient.invalidateQueries(['packages'])
  });

  const resetForm = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData(initialFormState);
  };

  const handleEdit = (pkg) => {
    // Populate form with package data
    const usdPrice = pkg.pricing?.find(p => p.currency === 'USD')?.amount || 0;
    const inrPrice = pkg.pricing?.find(p => p.currency === 'INR')?.amount || 0;
    
    setFormData({
      name: pkg.name,
      description: pkg.features_list ? pkg.features_list.join(', ') : pkg.description,
      billing_cycle: pkg.billing_cycle,
      duration_days: pkg.duration_days,
      max_devices: pkg.max_devices,
      price_usd: usdPrice,
      price_inr: inrPrice,
      features: pkg.features_list || [],
      can_autokill: !!pkg.can_autokill,
      can_isolate: !!pkg.can_isolate,
      can_quarantine: !!pkg.can_quarantine,
      can_schedule_scan: !!pkg.can_schedule_scan,
      has_vulnerability_scanner: !!pkg.has_vulnerability_scanner,
      has_watchtower: !!pkg.has_watchtower,
      has_export_logs: !!pkg.has_export_logs,
      has_proactive_hunting: !!pkg.has_proactive_hunting,
      has_threat_detection: !!pkg.has_threat_detection,
      has_router_detection: !!pkg.has_router_detection,
      has_supply_chain_detection: !!pkg.has_supply_chain_detection,
      has_ml_evasion_detection: !!pkg.has_ml_evasion_detection,
      has_soc_access: !!pkg.has_soc_access,
      has_mobile_protection: !!pkg.has_mobile_protection,
      has_spam_blocker: !!pkg.has_spam_blocker,
      has_anti_theft: !!pkg.has_anti_theft,
      has_overlay_guard: !!pkg.has_overlay_guard,
      has_netwall_pro: !!pkg.has_netwall_pro,
      has_crypto_guard: !!pkg.has_crypto_guard,
      has_immortal_mode: !!pkg.has_immortal_mode,
    });
    setEditingId(pkg.id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' }); 
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = {
      ...formData, 
      features: typeof formData.description === 'string' ? formData.description.split(',').map(s => s.trim()) : []
    };

    if (editingId) {
      updateMutation.mutate({ id: editingId, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Plan Configuration</h1>
          <p className="text-sm text-slate-500">Define subscription packages for license generation.</p>
        </div>
        <button onClick={() => { resetForm(); setShowForm(!showForm); }} className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center hover:bg-blue-700 shadow-lg shadow-blue-500/20">
          <Plus className="w-4 h-4 mr-2" /> {showForm ? 'Close Form' : 'Create New Plan'}
        </button>
      </div>

      {/* Create/Edit Form */}
      {showForm && (
        <div className="bg-white p-6 rounded-xl shadow-lg border border-blue-100 mb-8 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-1 h-full bg-blue-500"></div>
          <h3 className="font-bold text-lg mb-4 text-slate-800">{editingId ? 'Edit Package' : 'Configure New Package'}</h3>
          
          {/* Quick Presets (Only for Create) */}
          {!editingId && (
            <div className="flex space-x-2 mb-6">
              <button type="button" onClick={() => applyPreset(30, 'Monthly Basic', 'monthly')} className="px-3 py-1 bg-slate-100 text-slate-600 text-xs rounded-full hover:bg-blue-50 hover:text-blue-600 transition-colors border border-slate-200">1 Month (30 Days)</button>
              <button type="button" onClick={() => applyPreset(90, 'Quarterly Pro', 'one_time')} className="px-3 py-1 bg-slate-100 text-slate-600 text-xs rounded-full hover:bg-blue-50 hover:text-blue-600 transition-colors border border-slate-200">3 Months (90 Days)</button>
              <button type="button" onClick={() => applyPreset(365, 'Annual Gold', 'yearly')} className="px-3 py-1 bg-slate-100 text-slate-600 text-xs rounded-full hover:bg-blue-50 hover:text-blue-600 transition-colors border border-slate-200">1 Year (365 Days)</button>
            </div>
          )}

          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Package Name</label>
              <input 
                placeholder="e.g. Gold Plan - 1 Year" 
                className="w-full p-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" 
                required 
                value={formData.name}
                onChange={e => setFormData({...formData, name: e.target.value})} 
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
               <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Validity (Days)</label>
                  <input 
                    type="number" 
                    className="w-full p-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none font-mono" 
                    required 
                    value={formData.duration_days}
                    onChange={e => setFormData({...formData, duration_days: parseInt(e.target.value)})} 
                  />
               </div>
               <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Device Limit</label>
                  <input 
                    type="number" 
                    className="w-full p-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none font-mono" 
                    required 
                    value={formData.max_devices}
                    onChange={e => setFormData({...formData, max_devices: parseInt(e.target.value)})} 
                  />
               </div>
            </div>

            <div>
               <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Billing Type</label>
               <select 
                 className="w-full p-2.5 border border-slate-200 rounded-lg bg-white" 
                 value={formData.billing_cycle}
                 onChange={e => setFormData({...formData, billing_cycle: e.target.value})}
               >
                 <option value="one_time">One Time (Custom Days)</option>
                 <option value="monthly">Monthly Recurring</option>
                 <option value="yearly">Yearly Recurring</option>
                 <option value="lifetime">Lifetime Access</option>
               </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
               <div>
                 <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Price (USD)</label>
                 <div className="relative">
                   <span className="absolute left-3 top-2.5 text-slate-400">$</span>
                   <input type="number" className="w-full pl-6 p-2.5 border border-slate-200 rounded-lg" required value={formData.price_usd} onChange={e => setFormData({...formData, price_usd: parseFloat(e.target.value) || 0})} />
                 </div>
               </div>
               <div>
                 <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Price (INR)</label>
                 <div className="relative">
                   <span className="absolute left-3 top-2.5 text-slate-400">₹</span>
                   <input type="number" className="w-full pl-7 p-2.5 border border-slate-200 rounded-lg" required value={formData.price_inr} onChange={e => setFormData({...formData, price_inr: parseFloat(e.target.value) || 0})} />
                 </div>
               </div>
            </div>
            {/* --- FEATURE GATING SWITCHBOARD --- */}
            <div className="md:col-span-2 pt-6 border-t border-slate-100">
              <h4 className="text-sm font-black text-slate-800 uppercase tracking-widest mb-6 flex items-center">
                🛡️ Neural Core Feature Matrix
              </h4>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                
                {/* ================= 🖥️ DESKTOP & SERVER ENDPOINT ================= */}
                <div className="space-y-4">
                  <div className="flex items-center space-x-2 text-blue-600 border-b border-blue-100 pb-2">
                    <Monitor className="w-5 h-5" />
                    <h5 className="text-sm font-bold uppercase">Windows & Linux EDR</h5>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 bg-blue-50/30 p-4 rounded-2xl border border-blue-100">
                    {[
                      { id: 'has_threat_detection', label: 'Core Threat Intel' },
                      { id: 'can_autokill', label: 'Desktop/Mobile Auto-Kill (Termination)' },
                      { id: 'can_isolate', label: 'Desktop/Mobile Network Isolation' },
                      { id: 'can_quarantine', label: 'Quarantine Vault' },
                      { id: 'can_schedule_scan', label: 'Scheduled Scanning' },
                      { id: 'has_vulnerability_scanner', label: 'SBOM / Vulnerability' },
                      { id: 'has_proactive_hunting', label: 'Proactive Hunting' },
                      { id: 'has_watchtower', label: 'Watchtower Logs' },
                      { id: 'has_export_logs', label: 'SIEM Log Export' },
                      { id: 'has_offline_ai', label: 'Local AGI Model' },
                      { id: 'has_router_detection', label: 'Firmware/Router Guard' },
                      { id: 'has_supply_chain_detection', label: 'Supply Chain Guard' },
                      { id: 'has_ml_evasion_detection', label: 'ML Evasion Shield' },
                      { id: 'has_soc_access', label: 'AIRS AI Active (SOC) / AIRS Mobile AI' },
                    ].map(f => (
                      <div key={f.id} className="flex items-center justify-between p-2.5 bg-white rounded-xl border border-blue-200 shadow-sm hover:border-blue-400 transition-colors">
                        <span className="text-[10px] font-bold text-slate-700 uppercase leading-tight">{f.label}</span>
                        <input 
                          type="checkbox" 
                          checked={formData[f.id]} 
                          onChange={e => setFormData({...formData, [f.id]: e.target.checked})}
                          className="w-4 h-4 rounded text-blue-600 focus:ring-blue-500 cursor-pointer"
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* ================= 📱 MOBILE ENDPOINT ================= */}
                <div className="space-y-4">
                  <div className="flex items-center space-x-2 text-purple-600 border-b border-purple-100 pb-2">
                    <span className="text-xl">📱</span>
                    <h5 className="text-sm font-bold uppercase">Android & iOS XDR</h5>
                  </div>
                  
                  <div className="grid grid-cols-1 gap-3 bg-purple-50/30 p-4 rounded-2xl border border-purple-100">
                    {[
                      { id: 'has_mobile_protection', label: 'Core Mobile Shield' },
                      { id: 'has_spam_blocker', label: 'CommShield (Spam/Scam)' },
                      { id: 'has_anti_theft', label: 'Anti-Theft System' },
                      { id: 'has_overlay_guard', label: 'Neural Overlay Guard' },
                      { id: 'has_netwall_pro', label: 'NetWall DNS Firewall' },
                      { id: 'has_crypto_guard', label: 'Crypto Wallet Vault' },
                      { id: 'has_immortal_mode', label: 'Immortal Mode (MIUI Persistence)' },
                    ].map(f => (
                      <div key={f.id} className="flex items-center justify-between p-3 bg-white rounded-xl border border-purple-200 shadow-sm hover:border-purple-400 transition-colors">
                        <span className="text-[10px] font-bold text-slate-700 uppercase">{f.label}</span>
                        <input 
                          type="checkbox" 
                          checked={formData[f.id]} 
                          onChange={e => setFormData({...formData, [f.id]: e.target.checked})}
                          className="w-4 h-4 rounded text-purple-600 focus:ring-purple-500 cursor-pointer"
                        />
                      </div>
                    ))}
                  </div>
                  
                  <div className="p-4 bg-amber-50 rounded-xl border border-amber-200">
                    <p className="text-[10px] text-amber-700 font-medium leading-relaxed">
                      <strong>Note:</strong> Multi-platform keys automatically deduct from the total device limit upon first activation on any OS.
                    </p>
                  </div>
                </div>

              </div>
            </div>

            <div className="md:col-span-2">
               <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Features (Comma Separated)</label>
               <textarea 
                 placeholder="e.g. Real-time Protection, Priority Support, Cloud Backup" 
                 className="w-full p-2.5 border border-slate-200 rounded-lg h-20" 
                 value={formData.description}
                 onChange={e => setFormData({...formData, description: e.target.value})} 
               />
            </div>
            
            <div className="md:col-span-2 flex justify-end space-x-3 pt-4 border-t border-slate-100">
               <button type="button" onClick={resetForm} className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
               <button type="submit" className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 shadow-lg shadow-blue-500/30">
                 {editingId ? 'Update Package' : 'Create Package'}
               </button>
            </div>
          </form>
        </div>
      )}

      {/* Package Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {packages.map(pkg => (
          <div key={pkg.id} className="bg-white p-6 rounded-xl border border-slate-100 shadow-sm relative group hover:shadow-md transition-shadow">
            <button onClick={() => deleteMutation.mutate(pkg.id)} className="absolute top-4 right-4 text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity p-2 hover:bg-red-50 rounded-full">
              <Trash2 className="w-4 h-4" />
            </button>
            
            <div className="flex items-center mb-5">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center text-white mr-4 shadow-lg shadow-blue-500/20">
                <Package className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-bold text-slate-800 text-lg leading-tight">{pkg.name}</h3>
                <span className="inline-block mt-1 px-2 py-0.5 bg-slate-100 text-slate-500 text-[10px] font-bold uppercase tracking-wider rounded">
                  {pkg.billing_cycle.replace('_', ' ')}
                </span>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4 mb-6">
               <div className="bg-slate-50 p-3 rounded-lg border border-slate-100">
                 <span className="text-xs text-slate-400 font-bold uppercase block mb-1">Validity</span>
                 <span className="font-bold text-slate-700 flex items-center">
                    <Clock className="w-3 h-3 mr-1.5 text-blue-500"/> 
                    {pkg.duration_days} Days
                 </span>
               </div>
               <div className="bg-slate-50 p-3 rounded-lg border border-slate-100">
                 <span className="text-xs text-slate-400 font-bold uppercase block mb-1">Devices</span>
                 <span className="font-bold text-slate-700 flex items-center">
                    <Monitor className="w-3 h-3 mr-1.5 text-purple-500"/> 
                    {pkg.max_devices} Max
                 </span>
               </div>
            </div>

            <div className="flex justify-between items-center pt-4 border-t border-slate-100">
               <div className="flex flex-col">
                  {pkg.pricing && pkg.pricing.map((p, i) => (
                    <span key={i} className="text-sm font-medium text-slate-600">
                      {p.currency === 'USD' ? '$' : '₹'}{p.amount} <span className="text-xs text-slate-400">/ {p.currency}</span>
                    </span>
                  ))}
               </div>
               <button 
                 onClick={() => handleEdit(pkg)}
                 className="flex items-center text-xs font-bold text-blue-600 hover:text-blue-700 bg-blue-50 px-3 py-1.5 rounded-lg border border-blue-100"
               >
                 <Edit2 className="w-3 h-3 mr-1" /> EDIT
               </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PackagesPage;